import { Account, BackofficePersona } from '@/domain/usecases'
import { FeatureFlagClient, FeatureFlagType, getFlagValue } from '@/infra/feature-flag'
import { redirect } from 'react-router-dom'
import { PathConstants } from '../paths'

// ============================================
// TIPOS
// ============================================

export type GuardContext = {
  account: Account.Model
}

export type GuardResult =
  | { allowed: true }
  | { allowed: false; redirect?: string; error?: Error; reason: string }

export type Guard = (ctx: GuardContext) => GuardResult

// ============================================
// GUARD DE FEATURE FLAG
// ============================================

/**
 * Exige que uma feature flag esteja habilitada.
 * Aguarda o LaunchDarkly ficar pronto e usa o valor padrão caso ainda não esteja.
 */
export function requireFeatureFlag(
  flag: FeatureFlagType,
  fallbackRedirect: string = PathConstants.Routes.HOME.value
): GuardResult {
  // Aguarda o LaunchDarkly estar totalmente inicializado antes de checar as flags.
  // Evita condições de corrida em que loaders rodam antes das flags carregarem.
  if (!FeatureFlagClient.isReady()) {
    // Usa o valor padrão caso o LD ainda não esteja pronto
    const allowed = flag.defaultValue as boolean
    if (!allowed) {
      console.warn('[Guard] LaunchDarkly not ready, using default value (denied)')
      return {
        allowed: false,
        redirect: fallbackRedirect,
        reason: 'Feature flag not ready, using default value (denied)'
      }
    }
    console.warn('[Guard] LaunchDarkly not ready, using default value (allowed)')
    return { allowed: true }
  }

  const allowed = getFlagValue(flag)
  if (allowed) {
    return { allowed: true }
  }

  console.log(`[Guard] Feature flag ${flag.name} is disabled`)
  return {
    allowed: false,
    redirect: fallbackRedirect,
    reason: `Feature flag ${flag.name} is disabled`
  }
}

// ============================================
// GUARDS DE AUDIÊNCIA / PERSONA (conceito de domínio — V3)
// ============================================
//
// Acesso por PERSONA gateia o módulo BACKOFFICE. O módulo USER NÃO é gateado por
// persona nem por módulo — é gateado por CAPABILITY (posse de pass plan), via
// `userGuards.passPlanHolder`. Ver o "MODELO DE ACESSO" em
// `domain/usecases/account/account-profile.ts`.

/**
 * Exige que a conta tenha PELO MENOS UMA das personas de backoffice informadas.
 * Membership não-exclusivo — preserva o comportamento de flags independentes
 * (uma conta pode ser company-admin e internal ao mesmo tempo).
 */
export function requirePersona(...personas: BackofficePersona[]): Guard {
  return (ctx: GuardContext): GuardResult => {
    const accountPersonas = Account.getPersonas(ctx.account)
    const hasPersona = personas.some(persona => accountPersonas.includes(persona))
    if (hasPersona) {
      return { allowed: true }
    }

    console.log(`[Guard] Account does not have any persona: ${personas.join(', ')}`)
    return {
      allowed: false,
      redirect: PathConstants.Routes.HOME.value,
      reason: `Account does not have any of the required personas: ${personas.join(', ')}`
    }
  }
}

// ============================================
// COMPOSIÇÃO DE GUARDS
// ============================================

/**
 * Compõe múltiplos guards em um único guard.
 * Todos precisam passar para o guard composto passar.
 * Retorna a primeira falha encontrada caso algum guard falhe.
 */
export function composeGuards(...guards: Guard[]): Guard {
  return (ctx: GuardContext): GuardResult => {
    for (const guard of guards) {
      const result = guard(ctx)
      if (!result.allowed) {
        return result
      }
    }
    return { allowed: true }
  }
}

// ============================================
// EXECUÇÃO DE GUARD
// ============================================

/**
 * Executa um guard e retorna um Response de redirect quando o acesso é negado.
 * Retorna null quando o acesso é permitido.
 * Lança erro caso o guard retorne um erro.
 */
export function executeGuard(guard: Guard, ctx: GuardContext): Response | null {
  const result = guard(ctx)

  if (result.allowed) {
    return null
  }

  // Se o guard retornou um erro, lança-o
  if (result.error) {
    throw result.error
  }

  // Caso contrário, redireciona
  const redirectUrl = result.redirect || PathConstants.Routes.HOME.value
  console.log(`[Guard] Redirecting to ${redirectUrl}: ${result.reason}`)
  return redirect(redirectUrl)
}
